import type { MyCurationGoodsItem } from '@musinsa/services-curator';
import { twMerge } from 'tailwind-merge';
import { ProductInformationPrimitive as ProductInformation } from '../../common';
import { useTrackImpression } from '../../common/hooks/useTrackImpression';
import { ProductImageWrapper, useProductLinkNavigation } from '../../product';

interface MyCurationProductProps {
  className?: string;
  item: MyCurationGoodsItem;
  checked?: boolean;
  index?: number;
  children?: React.ReactNode;
  imageClassName?: string;
  informationClassName?: string;
  informmationChildren?: React.ReactNode;
  onClickCheckbox?: () => void;
  onClickCloseButton?: () => void;
  onClickLikeButton?: (item: MyCurationGoodsItem, nextIsLiked: boolean) => void;
  enableDeeplink?: boolean; // 29cm 딥링크 활성화 여부
  callbackClick?: (item: MyCurationGoodsItem) => void;
  callbackImpression?: (item: MyCurationGoodsItem) => void; // NOTE: impression 함수를 외부에서 주입받음.
}

export const MyCurationProduct = ({
  className,
  item,
  checked,
  index,
  children,
  imageClassName,
  onClickCheckbox,
  onClickCloseButton,
  onClickLikeButton,
  enableDeeplink = false,
  callbackClick,
  callbackImpression,
  ...rest
}: MyCurationProductProps) => {
  const navigateToProduct = useProductLinkNavigation();
  const { ref } = useTrackImpression<HTMLDivElement>({
    onImpression: () => {
      if (callbackImpression) callbackImpression(item);
    },
  });

  const onClickProduct = () => {
    if (enableDeeplink) {
      navigateToProduct(item.linkUrl);
      callbackClick?.(item);
      return;
    }

    // TODO: 추후 PDP 이동으로 변경 필요.
    onClickCheckbox?.();
    callbackClick?.(item);
  };

  const handleLikeButton = (_goodsNo: string | number, nextIsLiked: boolean) => {
    onClickLikeButton?.(item, nextIsLiked);
  };

  return (
    <div
      ref={ref}
      role="button"
      className={twMerge(
        'cursor-pointer relative w-full h-full overflow-hidden flex flex-col',
        'bg-neutral-color-white',
        className,
      )}
      tabIndex={0}
      {...rest}
    >
      <ProductImageWrapper
        className={imageClassName}
        item={item}
        checked={checked}
        index={index}
        onClickProduct={onClickProduct}
        onClickCheckbox={onClickCheckbox}
        onClickCloseButton={onClickCloseButton}
        onClickLikeButton={handleLikeButton}
      />
      <div className="flex-1">
        <ProductInformation.Container>
          <div>
            <ProductInformation.BrandName brandName={item.brandName} />
            <ProductInformation.GoodsName goodsName={item.goodsName} onClick={onClickProduct} />
          </div>

          <div className="flex flex-col">
            <ProductInformation.DiscountPriceRow
              finalPrice={item.finalPrice ?? item.price}
              finalDiscount={item.finalDiscount ?? item.salesRate ?? 0}
            />

            {item.hasOptionPrice && <ProductInformation.OptionalPriceTag />}

            {(item.existsCoupon || item.isExclusive || item.isLowestPrice) && (
              <ProductInformation.ProductBadgeContainer>
                {item.existsCoupon && <ProductInformation.CouponBadge />}
                {item.isExclusive && <ProductInformation.ExclusiveGoodsBadge />}
                {/* 단독 라벨이 있으면 최저가도전 미노출 (Figma 정책) */}
                {item.isLowestPrice && !item.isExclusive && <ProductInformation.LowestPriceBadge />}
              </ProductInformation.ProductBadgeContainer>
            )}

            <ProductInformation.SalesVolume totalQuantity={item.totalQuantity ?? 0} />
          </div>
        </ProductInformation.Container>
      </div>

      {children && <div className="mt-auto">{children}</div>}
    </div>
  );
};
